import React, { useState, useEffect, useMemo, useContext } from 'react';

import { differenceInMilliseconds, format, millisecondsToMinutes } from 'date-fns';
import locale from 'date-fns/locale/pt-BR';

import { Steps, Loading as LoadingComponent, Item, ImportantBox, Modal } from '~/components';
import history from '~/config/history';
import { Context as QueryContext } from '~/contexts/QueryContext';
import { PATHS } from '~/routes/paths';
import api, { Endpoints } from '~/services/api';
import WebSocket from '~/services/websocket';
import { capitalize } from '~/utils/strings';

import { Container, Texto, Titulo, TextoModal } from './styles';

export default function Acionamento() {
  const { params } = useContext(QueryContext);
  const [steps, setSteps] = useState([]);
  const [clinicas, setClinicas] = useState([]);
  const [clinicaSelecionada, setClinica] = useState(undefined);
  const [expiracao, setExpiracao] = useState(undefined);

  const data = useMemo(() => history?.location?.state, []);

  useEffect(() => {
    if (!data) {
      history.replace(PATHS.loading);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const getStatus = async () => {
      try {
        const { data: status } = await api.get(Endpoints.BUSCAR_STATUS + params.idAssistServico);

        setSteps(
          status.map(
            ({ status: { descricao }, dataTerminoStatus: dataStatus = null, ...rest }) => ({
              ...rest,
              name: descricao,
              isCompleted: !!dataStatus,
              date: dataStatus ? new Date(dataStatus) : undefined,
            })
          )
        );
      } catch (error) {
        console.error(error);
      }
    };

    if (params?.idAssistServico) getStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicas]);

  useEffect(() => {
    const getClinicas = async () => {
      try {
        const {
          data: { dataHoraExpirarIndicClinica: dateExpiration, clinicasList: _clinicas },
        } = await api.get(Endpoints.BUSCAR_CLINICAS + params.idAssistServico);

        const millisecondsToExpiration = differenceInMilliseconds(
          new Date(dateExpiration),
          new Date()
        );

        if (millisecondsToExpiration <= 0) {
          history.replace(PATHS.acionamento_expirado, data);
        } else {
          setClinicas(
            _clinicas.map(
              ({ nome, dataHoraDisponivel: date, logradouro: endereco, ...restClinica }) => ({
                ...restClinica,
                nome,
                endereco,
                date: new Date(date),
              })
            )
          );
          setExpiracao(millisecondsToExpiration);
        }
      } catch (error) {
        console.error(error);
      }
    };

    const websocket = WebSocket(params.idAssistServico);
    websocket.addEventListener('message', (event) => {
      if (event?.data === 'refresh') {
        getClinicas().then(() => {
          websocket?.close();
        });
      }
    });

    return () => {
      websocket?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let interval;
    if (expiracao !== undefined) {
      if (!interval) {
        interval = setInterval(() => {
          if (expiracao > 0) {
            setExpiracao((exp) => exp - 1000);
          } else {
            history.replace(PATHS.acionamento_expirado, data);
          }
        }, 1000);
      }
    }

    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiracao]);

  const handlePress = (idClinica) => {
    api
      .post(Endpoints.ACEITAR_CLINICA + idClinica, {})
      .then(() => {
        history.replace(PATHS.acionamento_finalizado, data);
      })
      .catch((error) => {
        console.error(error);
        history.replace(PATHS.notfound);
      });
  };

  const renderTextModal = () => {
    const { nomeCliente, nome, endereco, date } = clinicaSelecionada;
    return (
      <TextoModal>
        {nomeCliente}, a consulta será realizada na clínica <TextoModal isBold>{nome}</TextoModal>.
        <br />
        <TextoModal isBold>
          {endereco} no dia {format(date, "dd 'de' MMMM',' ", { locale })}
          {capitalize(format(date, "EEEE 'às' HH'h'mm", { locale }))}. Podemos confirmar?
        </TextoModal>
      </TextoModal>
    );
  };

  function renderLoading() {
    return <LoadingComponent height={285} width={285} />;
  }

  function renderClinicas() {
    return (
      <>
        <Titulo>Selecione o melhor horário ou a clínica mais próxima de sua residência</Titulo>
        {clinicas.map((clinica, index) => (
          <Item
            disabled={!expiracao}
            key={String(index)}
            data={clinica}
            onPress={(_clinica) => setClinica({ ..._clinica, nomeCliente: data?.nomeCliente })}
          />
        ))}

        {expiracao > 0 && (
          <>
            <ImportantBox>
              Em casos de inatividade o serviço irá expirar em {millisecondsToMinutes(expiracao)}{' '}
              minutos.
            </ImportantBox>
          </>
        )}

        <Modal
          isVisible={!!clinicaSelecionada}
          renderText={renderTextModal}
          onPress={() => handlePress(clinicaSelecionada.idClinica)}
          buttonText='Confirmar'
          onClose={() => {
            setClinica(undefined);
          }}
        />
      </>
    );
  }

  return (
    <Container>
      <Texto>
        Olá, <Texto isBold>{data?.nomeCliente}</Texto>! Seu atendimento foi aberto com sucesso.
        Acompanhe o andamento da solicitação da{' '}
        <Texto isBold>
          Assistência: {data?.idAssistencia}/{data?.numServico}
        </Texto>
        .
        {clinicas.length === 0 && (
          <>
            <br />
            <br />
            Sua solicitação é <Texto isBold>{data?.nomeServico}</Texto>, aguarde um instante,
            estamos providenciando as melhores opções e vamos lhe retornar por aqui mesmo.
          </>
        )}
      </Texto>

      <Steps steps={steps} />

      {clinicas.length === 0 ? renderLoading() : renderClinicas()}
    </Container>
  );
}
